import { Receipt } from "@/components/icons";
import { Badge, Skeleton } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { OrderListItemDTO } from "@/modules/orders/domain/types";
import {
  getOrderStatusMeta,
  orderStatusLabel,
} from "@/modules/orders/presentation/ui/order-status";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Link } from "@tanstack/react-router";

type RecentOrdersTodayProps = {
  orders?: OrderListItemDTO[];
  isLoading?: boolean;
  className?: string;
};

export function RecentOrdersToday({
  orders = [],
  isLoading,
  className,
}: RecentOrdersTodayProps) {
  return (
    <div
      className={cn(
        "brand-card flex min-h-0 flex-col gap-4 p-4 sm:p-5",
        className,
      )}
      data-tourid="dashboard-recent-orders"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-lg">ອໍເດີ້ມື້ນີ້</h2>
          <p className="text-muted-foreground text-sm">
            ລາຍການອໍເດີ້ທີ່ສ້າງໃນມື້ນີ້
          </p>
        </div>
        <Link
          to="/app/orders"
          className="shrink-0 self-start text-muted-foreground text-sm underline-offset-4 hover:text-foreground hover:underline sm:self-auto"
        >
          ເບິ່ງທັງໝົດ
        </Link>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
          <Receipt className="size-8 opacity-40" />
          <p className="text-sm">ຍັງບໍ່ມີອໍເດີ້ມື້ນີ້</p>
        </div>
      ) : (
        <ul className="flex min-h-0 flex-1 flex-col gap-2">
          {orders.map((order) => {
            const statusMeta = getOrderStatusMeta(order.status, order.paymentType);
            return (
              <li key={order.id}>
                <Link
                  to="/app/orders/$id"
                  params={{ id: order.id }}
                  className="flex flex-col gap-2 rounded-xl border border-transparent px-3 py-2 transition-colors hover:border-secondary/20 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm">
                        {order.orderNumber}
                      </span>
                      <Badge variant={statusMeta.variant}>
                        {orderStatusLabel(order.status, order.paymentType)}
                      </Badge>
                    </div>
                    <p className="truncate text-muted-foreground text-sm">
                      {order.customerName}
                      {order.customerPhone ? ` · ${order.customerPhone}` : ""}
                    </p>
                  </div>
                  <div className="shrink-0 text-left text-sm sm:text-right">
                    <p className="font-medium tabular-nums">
                      {formatLak(order.total)}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {formatDateTimeLocal(order.createdAt)}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
